// =====================================================================
// src/screens/super-admin/lib/businesses.js
//
// Admin-side READ layer for /businesses. Deliberately separate from
// src/lib/v2/firestore/businesses.js because that helper hardcodes
// status IN ['verified', 'pending_verification'] for consumer use — admin
// needs unrestricted access including 'suspended' and any future statuses.
//
// The Firestore rule at /businesses/{id} line ~501 has an isPlatformAdmin()
// escape hatch that allows admin to read any status. That helper was updated
// in this same session to accept superAdmin===1, so these queries are safe.
//
// COST DISCIPLINE (banked as Phase 2 principle):
//   - Every list is paginated (limit(25) default, cursor-based load-more)
//   - No unbounded .get() on the collection
//   - Search filters loaded page client-side, no cross-collection queries
//   - Detail-view reads 0 additional docs (uses list result)
// =====================================================================

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { openOrCreateSupportChat } from '../../../lib/db';
import { sendAdminMessage } from './supportChats.js';

// Sentinel returned by listBusinessesForAdmin when the "All" filter is
// active. Callers pass this (or omit statusFilter) to skip the status
// where-clause entirely — needed because Firestore rejects where('status',
// '==', undefined) with a runtime error.
export const STATUS_FILTER_ALL = null;

// Default page size. Kept small so a session's total reads stay predictable
// even if the admin clicks Load more several times. Rajasekhar's cost
// discipline: no unbounded reads.
export const ADMIN_PAGE_SIZE = 25;

// ============================================================
// listBusinessesForAdmin({ statusFilter, cursor })
// ============================================================
//
// Reads a paginated slice of /businesses filtered by status (optional) and
// ordered by createdAt DESC (newest first — matches admin mental model for
// review-recent-activity workflows).
//
// Args:
//   statusFilter (string | null) — one of the /businesses.status values
//     (verified / pending_verification / suspended / ...) OR null/undefined
//     to skip filtering. Passing STATUS_FILTER_ALL is equivalent to null.
//   cursor (DocumentSnapshot | null) — the last doc from the previous page.
//     Pass null on first call; pass the returned `lastDoc` on subsequent
//     "Load more" clicks. Firestore's startAfter() gives us cheap cursor
//     pagination with no offset scan.
//
// Returns:
//   {
//     businesses: Array<{ id, ...fields }>,
//     lastDoc:    DocumentSnapshot | null   // pass back for load-more, null=end
//     hasMore:    boolean                    // true if the page filled to the limit
//   }
//
// Firestore cost per call: exactly ADMIN_PAGE_SIZE reads (or fewer on the
// last page).
//
// Required indexes:
//   - businesses (status ASC, createdAt DESC, __name__ DESC) — added to
//     firestore.indexes.json in this same session, only needed for filtered
//     queries. Unfiltered ("All") queries use the auto-index on createdAt.
//
// Rules alignment:
//   - The read rule at /businesses/{id} allows admin (isPlatformAdmin())
//     to read any status. Non-admin callers hitting this helper would
//     get permission-denied because there's no status constraint filtering
//     to rule-allowed values.
//
// Throws: any Firestore error (rules failure, missing index, network).
//
export async function listBusinessesForAdmin({ statusFilter = STATUS_FILTER_ALL, cursor = null } = {}) {
  const constraints = [];

  if (statusFilter && statusFilter !== STATUS_FILTER_ALL) {
    constraints.push(where('status', '==', statusFilter));
  }

  // Newest first. Ordering is stable across pages because Firestore falls
  // back to __name__ as tiebreaker (declared in the composite index).
  constraints.push(orderBy('createdAt', 'desc'));

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  constraints.push(limit(ADMIN_PAGE_SIZE));

  const q = query(collection(db, 'businesses'), ...constraints);
  const snap = await getDocs(q);

  const businesses = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  const hasMore = snap.docs.length === ADMIN_PAGE_SIZE;

  return { businesses, lastDoc, hasMore };
}

// ============================================================
// STATUS_FILTER_OPTIONS
// ============================================================
//
// The user-facing filter chips render from this list, so keeping it here
// (next to the data helper) means the label/value/pill mapping is defined
// once. Order is the sidebar chip order in the prototype's Businesses page.
//
// slug: URL/query value (STATUS_FILTER_ALL for "All", otherwise the raw
//       Firestore status field value — the values we actually filter on).
// label: user-visible chip text.
// pillClass: CSS class on the row status pill, so status text renders in
//            the right color everywhere without a switch statement in JSX.
//
// Design decision (banked): filter chips reflect actual /businesses.status
// values only, no derived statuses. Trial / Free Period / Lapsed / Active
// (as in the prototype) require deriving from activated + trialStartedAt +
// trialDays + current time; those derive-status filters land when the
// subscription-model session ships. Session 2 = simple and real.
export const STATUS_FILTER_OPTIONS = [
  { slug: STATUS_FILTER_ALL, label: 'All', pillClass: null },
  { slug: 'verified', label: 'Verified', pillClass: 'sa-pill-active' },
  { slug: 'pending_verification', label: 'Pending verification', pillClass: 'sa-pill-pending' },
  { slug: 'suspended', label: 'Suspended', pillClass: 'sa-pill-suspended' },
  { slug: 'rejected', label: 'Rejected', pillClass: 'sa-pill-rejected' },
];

// Look up the pill CSS class for a given status value. Falls back to a
// neutral pill for statuses we don't have a chip for (defensive — if the
// data has a status we don't recognize, the row still renders).
export function pillClassForStatus(status) {
  const opt = STATUS_FILTER_OPTIONS.find((o) => o.slug === status);
  return opt?.pillClass || 'sa-pill-suspended';
}

// User-visible label for a status value.
export function labelForStatus(status) {
  const opt = STATUS_FILTER_OPTIONS.find((o) => o.slug === status);
  return opt?.label || status || '—';
}

// ============================================================
// approveBusiness({ businessId, adminUid })
// ============================================================
//
// Verification-queue Approve action. Writes:
//   - status:       'verified'
//   - verified:     true
//   - verifiedAt:   serverTimestamp()
//   - verifiedBy:   adminUid
//
// IT USED TO SKIP `status`, AND THAT WAS A BUG. The original note here read:
// "Does NOT change status. Per product decision, businesses are ALREADY on
// the consumer directory during pending_verification... Approve just flips
// verified:true so the ✓ badge appears — no directory visibility change."
//
// That reasoning was about DIRECTORY VISIBILITY and was correct at the time.
// Session 6b later made transactability depend on the status enum:
//
//     isTransactableData(biz) =
//       biz.status == 'verified'
//       || (biz.status == 'pending_verification' && still inside free trial)
//
// So an approved business kept status 'pending_verification', and the moment
// its trial expired Firestore refused to create orders for it — even though
// the ✓ badge said verified and the merchant had paid. The two decisions were
// made in different sessions and never reconciled. Symptoms, all one cause:
//   - the business never left the Verification Queue (queue filters on status)
//   - the ✓ badge appeared anyway (consumer UI reads the `verified` boolean)
//   - placing an order failed with permission-denied (rules read `status`)
//
// `status` is the lifecycle enum: rejectBusiness writes 'rejected', suspend
// writes 'suspended'. Approve was the only transition that never wrote it.
// Now it does, and the boolean stays for the consumer badge.
//
// Directory visibility is unchanged: the read rule already allows both
// 'verified' and 'pending_verification' publicly.
//
// BACKFILL: businesses approved before this fix carry verified:true with
// status:'pending_verification'. Re-approving them from the queue is
// idempotent and repairs the doc.
//
// Args:
//   businessId (string, required) — the /businesses doc id
//   adminUid   (string, required) — the acting admin's UID for audit
//
// Firestore cost: 1 write.
//
// Rules alignment: the /businesses update rule's admin branch (checked
// via isPlatformAdmin()) permits any field write except ownerId,
// createdAt, connectCount, orderCount. All fields we set here are
// permitted.
//
// Throws: any Firestore error (rules failure, network). Callers should
// catch and surface to the admin.
//
export async function approveBusiness({ businessId, adminUid }) {
  if (!businessId) throw new Error('approveBusiness: businessId is required');
  if (!adminUid) throw new Error('approveBusiness: adminUid is required');

  const ref = doc(db, 'businesses', businessId);
  await updateDoc(ref, {
    // The enum the Firestore rules actually read (isTransactableData).
    status: 'verified',
    // The boolean the consumer UI reads for the ✓ badge.
    verified: true,
    verifiedAt: serverTimestamp(),
    verifiedBy: adminUid,
  });
}

// ============================================================
// rejectBusiness({ businessId, adminUid, reason })
// ============================================================
//
// Verification-queue Reject action. Writes:
//   - status:          'rejected'    (hard reject — new status value)
//   - rejectedReason:  reason        (admin-typed free text)
//   - rejectedAt:      serverTimestamp()
//   - rejectedBy:      adminUid
//
// Per product decision (banked): rejection is a hard dead-end. The owner
// cannot re-submit — if they want to try again they register a new
// business. `status: 'rejected'` is a new status value not previously in
// use; the /businesses read rule does NOT list 'rejected' in the public
// branch, so rejected businesses disappear from the consumer directory
// automatically. Owner + admin retain read access via their respective
// clauses in the read rule.
//
// Args:
//   businessId (string, required)
//   adminUid   (string, required)
//   reason     (string, required, non-empty) — admin-typed rejection reason
//
// Firestore cost: 1 write.
//
// Rules alignment: admin branch permits status writes.
//
// Throws: any Firestore error, plus a validation error if reason is empty.
//
export async function rejectBusiness({ businessId, adminUid, reason }) {
  if (!businessId) throw new Error('rejectBusiness: businessId is required');
  if (!adminUid) throw new Error('rejectBusiness: adminUid is required');
  const trimmed = (reason || '').trim();
  if (!trimmed) throw new Error('rejectBusiness: a rejection reason is required');

  const ref = doc(db, 'businesses', businessId);
  await updateDoc(ref, {
    status: 'rejected',
    rejectedReason: trimmed,
    rejectedAt: serverTimestamp(),
    rejectedBy: adminUid,
  });
}

// ============================================================
// sendRequestInfoMessage({ businessId, ownerId, adminUid, message })
// ============================================================
//
// Verification-queue Request Info action (Session 5b). Composes two
// Session 5a helpers:
//   1. openOrCreateSupportChat({ ownerUid }) — ensures the support chat
//      between the business owner and Aangan Support exists. Idempotent;
//      creates on first call, no-op on subsequent calls except for
//      chatType/ownerName refresh writes.
//   2. sendAdminMessage({ chatId, adminUid, text }) — posts the request
//      into that chat with senderRole:'support' + fromId:adminUid. The
//      business owner sees it as an incoming "Aangan Support" message
//      the next time they open their support chat modal.
//
// Per product decision (banked):
//   - Business STAYS in the pending_verification queue after Request Info
//     is sent. Verification state is unchanged — requesting information
//     doesn't resolve verification, it delays it. Admin can later
//     Approve or Reject the same business once the owner replies.
//   - No new fields on the business doc (no "awaiting owner reply"
//     marker). Chose simple over structured. Adds correctness without
//     schema surface area.
//
// The `businessId` param isn't strictly needed for the write itself, but
// it's accepted so the audit trail (once we build it in a future admin
// audit-log session) has business context. adminUid is captured on the
// message doc's fromId (per sendAdminMessage's contract).
//
// Args:
//   businessId (string, required) — the business the request is about
//   ownerId    (string, required) — the business owner's UID
//   adminUid   (string, required) — the acting admin's UID (from authUser)
//   message    (string, required, non-empty trimmed) — the request text
//
// Firestore cost:
//   - openOrCreateSupportChat: 1 read + 0-1 writes (create or merge patch)
//   - sendAdminMessage:        2 writes (message add + chat setDoc merge)
//   - Total: 1 read + 2-3 writes
//
// Rules alignment:
//   - /chats create rule was extended in this session to allow platform
//     admins to create support chats they aren't participants of
//     (chatType=='support' required).
//   - /chats update + /chats/{id}/messages read/create rules were
//     extended in Session 5a to allow admin access on chatType=='support'
//     chats.
//
// Throws: validation errors + any Firestore error.
//
export async function sendRequestInfoMessage({ businessId, ownerId, adminUid, message }) {
  if (!businessId) throw new Error('sendRequestInfoMessage: businessId is required');
  if (!ownerId) throw new Error('sendRequestInfoMessage: ownerId is required');
  if (!adminUid) throw new Error('sendRequestInfoMessage: adminUid is required');
  const trimmed = (message || '').trim();
  if (!trimmed) throw new Error('sendRequestInfoMessage: a request message is required');

  // Ensure the support chat exists. We don't have ownerName here (business
  // doc doesn't carry it, and looking it up would add a /users read on a
  // discrete admin action). Empty ownerName is fine — the admin support
  // inbox renders the raw UID as fallback, and ownerName gets populated
  // with the owner's real name the first time they open their Aangan
  // Support modal (openOrCreateSupportChat refreshes ownerName each open).
  const { chatId } = await openOrCreateSupportChat({ ownerUid: ownerId });

  // Send the request message. sendAdminMessage tags it with
  // senderRole:'support' so display logic on both sides renders it
  // as an admin/Aangan Support message.
  await sendAdminMessage({ chatId, adminUid, text: trimmed });

  return { chatId };
}

// ============================================================
// activateBusiness({ businessId, adminUid, tier, validTill, overrideValidTill, paymentRef, note })
// ============================================================
//
// Session 6a — the subscription/activation write path. Called from the
// Activation modal when admin confirms a paid activation. Writes the
// full activation payload as flat fields on the business doc.
//
// SCHEMA (10 fields):
//   activated:                    true
//   activatedAt:                  serverTimestamp() — also serves as
//                                 payment received date (Q_C decision:
//                                 no back-dating, so activatedAt IS the
//                                 payment date)
//   activatedBy:                  adminUid (audit)
//   activationTier:               tier.id (e.g. '199')
//   activationAmount:             tier.amount (e.g. 199)
//   activationDurationMonths:     tier.durationMonths (e.g. 1)
//   activationValidTill:          effective valid-till date (validTill
//                                 OR overrideValidTill if provided)
//   activationValidTillOverride:  overrideValidTill or null. Only populated
//                                 when admin used the override — audit
//                                 signal so activity log can flag exceptions.
//   activationPaymentRef:         UPI ref / bank txn ID (free text)
//   activationNote:               optional free-text
//
// REACTIVATION: this helper overwrites the same fields. No append-only
// history collection this session — history lives in the (future)
// activity log surface. Overwriting is fine for the current MVP where
// we care about "is this business currently active?" more than
// "how many times has it been renewed?"
//
// FEATURE GATING (deferred to Sessions 6b/6c):
//   Client-side (merchant/customer surfaces): read activationValidTill,
//   derive isActive = activated && validTill > now.
//   Rules-side (backend enforcement): extend /businesses update rule
//   and product/wall/etc rules to gate on isActive. This session ships
//   the write path only.
//
// Args:
//   businessId          (string, required)
//   adminUid            (string, required)
//   tier                (object, required) — { id, amount, durationMonths, label }
//   validTill           (Date, required) — the auto-computed valid-till from
//                                          today + tier.durationMonths
//   overrideValidTill   (Date | null) — if admin used override, this is the
//                                       override date; else null
//   paymentRef          (string, required, non-empty) — payment reference for audit
//   note                (string, optional) — free-text note
//
// Firestore cost: 1 write.
//
// Rules alignment: the /businesses update rule's admin branch permits
// any field write except ownerId/createdAt/connectCount/orderCount.
// All 10 activation fields fall inside that permission surface — zero
// rules change needed on the business doc for these writes (the /config
// rules change is separate — for the modal's tier fetch).
//
// Throws: validation errors + any Firestore error.
export async function activateBusiness({
  businessId,
  adminUid,
  tier,
  validTill,
  overrideValidTill,
  paymentRef,
  note,
}) {
  if (!businessId) throw new Error('activateBusiness: businessId is required');
  if (!adminUid) throw new Error('activateBusiness: adminUid is required');
  if (!tier || !tier.id) throw new Error('activateBusiness: tier is required');
  if (typeof tier.amount !== 'number') throw new Error('activateBusiness: tier.amount must be a number');
  if (typeof tier.durationMonths !== 'number') throw new Error('activateBusiness: tier.durationMonths must be a number');
  if (!(validTill instanceof Date)) throw new Error('activateBusiness: validTill must be a Date');
  if (Number.isNaN(validTill.getTime())) throw new Error('activateBusiness: validTill is invalid');
  const paymentRefTrimmed = (paymentRef || '').trim();
  if (!paymentRefTrimmed) throw new Error('activateBusiness: paymentRef is required');
  const noteTrimmed = (note || '').trim();

  const overrideValid = overrideValidTill instanceof Date && !Number.isNaN(overrideValidTill.getTime())
    ? overrideValidTill
    : null;
  const effectiveValidTill = overrideValid || validTill;

  const ref = doc(db, 'businesses', businessId);

  // Session 6d — activate + emit a merchant-notification sidecar event
  // atomically.
  //
  // The activation write and the "tell the merchant" trigger are committed
  // together via a batch, so a merchant is never notified for an activation
  // that didn't land (and an activation never lands without queuing the
  // notify). We CANNOT hang an onDocumentUpdated trigger on
  // /businesses/{bizId} — it fails to deploy in asia-south1 with the
  // misleading "region not compatible" error (Eventarc validation conflict
  // with onWallPostCreated on businesses/{bizId}/posts/{id}). So activation
  // fans out through a throwaway event doc the same way "Notify when open"
  // does via businessOpenEvents.
  //
  // Event schema — firestore.rules validates keys().hasOnly:
  //   { businessId, setAt, triggeredBy }
  // The onActivationEvent Cloud Function reads name / logoUrl / ownerId /
  // activationValidTill straight from the freshly-updated business doc, so
  // nothing else needs to travel in the event payload.
  const batch = writeBatch(db);

  batch.update(ref, {
    activated: true,
    activatedAt: serverTimestamp(),
    activatedBy: adminUid,
    activationTier: tier.id,
    activationAmount: tier.amount,
    activationDurationMonths: tier.durationMonths,
    activationValidTill: effectiveValidTill,
    activationValidTillOverride: overrideValid,
    activationPaymentRef: paymentRefTrimmed,
    activationNote: noteTrimmed,
  });

  const eventRef = doc(collection(db, 'businessActivationEvents'));
  batch.set(eventRef, {
    businessId,
    setAt: serverTimestamp(),
    triggeredBy: adminUid,
  });

  await batch.commit();
}
